const MAPS_SCRIPT_ID = "google-maps-js";
const MAPS_CALLBACK_NAME = "__orbiGoogleMapsReady";

function base64ToBytes(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Decrypt AES-256-CBC payload from /api/config.
 * Format (base64): iv (16 bytes) + ciphertext.
 */
export async function decryptClientData(encoded, clientKey) {
  if (!encoded || !clientKey) {
    throw new Error("Missing encrypted data or client key");
  }

  const raw = base64ToBytes(encoded);
  if (raw.length < 17) {
    throw new Error("Invalid encrypted payload");
  }

  const iv = raw.slice(0, 16);
  const cipherText = raw.slice(16);
  const keyMaterial = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(clientKey),
  );
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyMaterial,
    { name: "AES-CBC" },
    false,
    ["decrypt"],
  );
  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-CBC", iv },
    cryptoKey,
    cipherText,
  );

  return new TextDecoder().decode(decrypted);
}

function injectMapsScript(apiKey, libraries = "places") {
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.places) {
      resolve(window.google);
      return;
    }

    const existing = document.getElementById(MAPS_SCRIPT_ID);
    if (existing) {
      const started = Date.now();
      const waitForPlaces = () => {
        if (window.google?.maps?.places) {
          resolve(window.google);
          return;
        }
        if (Date.now() - started > 15000) {
          reject(new Error("Timed out waiting for Places library"));
          return;
        }
        window.setTimeout(waitForPlaces, 50);
      };
      waitForPlaces();
      return;
    }

    window[MAPS_CALLBACK_NAME] = () => {
      delete window[MAPS_CALLBACK_NAME];
      if (window.google?.maps?.places) {
        resolve(window.google);
        return;
      }
      reject(new Error("Places library failed to load"));
    };

    const script = document.createElement("script");
    script.id = MAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    script.src =
      `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}` +
      `&libraries=${encodeURIComponent(libraries)}` +
      `&callback=${MAPS_CALLBACK_NAME}`;
    script.onerror = () => {
      delete window[MAPS_CALLBACK_NAME];
      reject(new Error("Failed to load Maps script"));
    };
    document.head.appendChild(script);
  });
}

let mapsLoadPromise = null;

/**
 * Fetch encrypted client config, decrypt, load Maps JS.
 * Decrypted slots: a (android), i (ios), w (web).
 */
export function loadGoogleMaps() {
  if (mapsLoadPromise) {
    return mapsLoadPromise;
  }

  mapsLoadPromise = (async () => {
    if (window.google?.maps?.places) {
      return window.google;
    }

    const domain = (process.env.REACT_APP_APP_DOMAIN || "").trim();
    const clientKey = (
      process.env.REACT_APP_CLIENT_KEY ||
      process.env.REACT_APP_MAP_SECURITY_KEY ||
      ""
    ).trim();

    if (!domain) {
      throw new Error("REACT_APP_APP_DOMAIN is not configured");
    }
    if (!clientKey) {
      throw new Error("REACT_APP_CLIENT_KEY is not configured");
    }

    const response = await fetch(`${domain}/api/config`, {
      headers: {
        Accept: "application/json",
        "X-Client-Key": clientKey,
      },
    });

    if (response.status === 401) {
      throw new Error("Unauthorized");
    }

    const payload = await response.json();
    if (!response.ok || !payload?.success || !payload?.data) {
      throw new Error("Unable to load client config");
    }

    const decrypted = await decryptClientData(payload.data, clientKey);
    let config;
    try {
      config = JSON.parse(decrypted);
    } catch (error) {
      throw new Error("Invalid client config payload");
    }

    // console.log("[config] decrypted payload:", config);
    // console.log("[config] web key (w):", config?.w);

    const apiKey = config?.w;
    if (!apiKey) {
      throw new Error("Client config field missing");
    }

    return injectMapsScript(apiKey);
  })().catch((error) => {
    mapsLoadPromise = null;
    console.error("Maps load failed:", error);
    throw error;
  });

  return mapsLoadPromise;
}
